/**
 * Merge translations[lang] with site_content from the API.
 *
 * CRITICAL: baseT is already localized (it IS translations.fr or translations.en).
 * When lang === "en", we must NOT spread FR CMS fields over EN base translations.
 *
 * Strategy per lang:
 *  - lang === "fr"  → use CMS value `field` (FR) with baseT.field as fallback.
 *  - lang === "en"  → use CMS value `field_en` (if set) with baseT.field as fallback
 *                     (baseT.field is already EN because baseT = translations.en).
 *                     We NEVER fall back to `field` (FR) when lang is EN.
 */

const pick = (obj, baseVal, key, lang) => {
  if (!obj) return baseVal;
  if (lang === "en") {
    const enVal = obj[`${key}_en`];
    if (enVal !== undefined && enVal !== null && enVal !== "") return enVal;
    return baseVal;
  }
  const frVal = obj[key];
  if (frVal !== undefined && frVal !== null && frVal !== "") return frVal;
  return baseVal;
};

/** Merge one CMS item (possibly FR-only) with its EN base sibling from i18n. */
const mergeItem = (override, baseItem, lang, textKeys) => {
  if (!override) return baseItem || {};
  const out = { ...override };
  for (const key of textKeys) {
    out[key] = pick(override, baseItem?.[key], key, lang);
  }
  return out;
};

export function mergeTranslationsWithContent(baseT, content, lang) {
  if (!content) return baseT;

  const t = { ...baseT };

  // Hero
  if (content.hero) {
    t.hero = {
      ...baseT.hero,
      tag: pick(content.hero, baseT.hero.tag, "tag", lang),
      title1: pick(content.hero, baseT.hero.title1, "title1", lang),
      title2: pick(content.hero, baseT.hero.title2, "title2", lang),
      title3: pick(content.hero, baseT.hero.title3, "title3", lang),
      italic: pick(content.hero, baseT.hero.italic, "italic", lang),
      intro: pick(content.hero, baseT.hero.intro, "intro", lang),
    };
  }

  // Manifesto / About
  if (content.manifesto) {
    t.about = {
      ...baseT.about,
      lead: pick(content.manifesto, baseT.about.lead, "lead", lang),
      pillars: (baseT.about.pillars || []).map((basePillar, i) => {
        const override = content.manifesto.pillars?.[i];
        if (!override) return basePillar;
        return {
          ...basePillar,
          t: pick(override, basePillar.t, "t", lang),
          d: pick(override, basePillar.d, "d", lang),
        };
      }),
    };
  }

  // Services
  if (content.services?.items) {
    t.services = {
      ...baseT.services,
      items: (baseT.services.items || []).map((baseItem, i) => {
        const override = content.services.items[i];
        if (!override) return baseItem;
        const merged = mergeItem(override, baseItem, lang, ["t", "d"]);
        // tags list has its own _en key (array)
        merged.tags = lang === "en"
          ? (Array.isArray(override.tags_en) && override.tags_en.length ? override.tags_en : baseItem.tags)
          : (Array.isArray(override.tags) && override.tags.length ? override.tags : baseItem.tags);
        return merged;
      }),
    };
  }

  // Projects — match CMS items to baseT items by slug (i18n.js now provides slugs).
  if (content.projects?.items) {
    const baseItems = baseT.projects.items || [];
    const baseBySlug = {};
    baseItems.forEach((b) => { if (b.slug) baseBySlug[b.slug] = b; });
    t.projects = {
      ...baseT.projects,
      items: content.projects.items.map((p, i) => {
        const baseItem = baseBySlug[p.slug] || baseItems[i] || {};
        return mergeItem(p, baseItem, lang, ["t", "cat", "d", "longDesc", "client"]);
      }),
    };
  }

  // Testimonials — no baseT equivalent list, FR text is the canonical source
  if (content.testimonials?.items) {
    t.testimonials = {
      ...baseT.testimonials,
      items: content.testimonials.items.map((r) => ({
        ...r,
        text: lang === "en" && r.text_en ? r.text_en : r.text,
        date: lang === "en" && r.date_en ? r.date_en : r.date,
        location: lang === "en" && r.location_en ? r.location_en : r.location,
      })),
    };
  }

  // Team — no i18n baseT equivalent; fall back to FR text when no _en
  if (content.team) {
    t._team = {
      heading: {
        title: lang === "en" && content.team.heading?.title_en
          ? content.team.heading.title_en
          : content.team.heading?.title,
        intro: lang === "en" && content.team.heading?.intro_en
          ? content.team.heading.intro_en
          : content.team.heading?.intro,
      },
      members: (content.team.members || []).map((m) => ({
        ...m,
        role: lang === "en" && m.role_en ? m.role_en : m.role,
        bio: lang === "en" && m.bio_en ? m.bio_en : m.bio,
        quote: lang === "en" && m.quote_en ? m.quote_en : m.quote,
        tags: lang === "en" && Array.isArray(m.tags_en) && m.tags_en.length ? m.tags_en : m.tags,
      })),
    };
  }

  if (content.contact_info) {
    t._contact_info = content.contact_info;
  }

  // FAQ — replace with CMS content when available; falls back to baseT.faq
  if (content.faq) {
    const head = content.faq.heading || {};
    const items = Array.isArray(content.faq.items) ? content.faq.items : [];
    const pick = (k) => (lang === "en" && head[`${k}_en`] ? head[`${k}_en`] : head[k]);
    t.faq = {
      ...baseT.faq,
      label: pick("label") || baseT.faq?.label,
      title: pick("title") || baseT.faq?.title,
      titleItalic: pick("title_italic") || baseT.faq?.titleItalic,
      intro: pick("intro") || baseT.faq?.intro,
      items: items.length
        ? items.map((it) => ({
            q: lang === "en" && it.q_en ? it.q_en : it.q,
            a: lang === "en" && it.a_en ? it.a_en : it.a,
          }))
        : (baseT.faq?.items || []),
    };
  }

  return t;
}

export function resolveProjectMeta(content, fallbackSlugs, fallbackImages, fallbackDetails, fallbackGalleries, lang = "fr") {
  if (!content?.projects?.items) {
    return { slugs: fallbackSlugs, images: fallbackImages, details: fallbackDetails, galleries: fallbackGalleries };
  }
  const items = content.projects.items;
  const pickLocalized = (p, key, fallbackVal) => {
    if (lang === "en") {
      const en = p[`${key}_en`];
      if (en !== undefined && en !== null && en !== "") return en;
      return fallbackVal;
    }
    return p[key] ?? fallbackVal;
  };
  // Auto-translate common French units to English when no _en override is set.
  const translateUnits = (v) => {
    if (lang !== "en" || !v || typeof v !== "string") return v;
    return v
      .replace(/m²/gi, "sqm")
      .replace(/(\d+(?:[.,]\d+)?)\s*mois\b/gi, "$1 months")
      .replace(/(\d+(?:[.,]\d+)?)\s*semaines?\b/gi, "$1 weeks")
      .replace(/(\d+(?:[.,]\d+)?)\s*jours?\b/gi, "$1 days")
      .replace(/(\d+(?:[.,]\d+)?)\s*ans?\b/gi, "$1 years");
  };
  const localizeUnit = (p, key, fallbackVal) => {
    if (lang === "en") {
      const en = p[`${key}_en`];
      if (en !== undefined && en !== null && en !== "") return en;
      return translateUnits(p[key] ?? fallbackVal);
    }
    return p[key] ?? fallbackVal;
  };
  return {
    slugs: items.map((p, i) => p.slug || fallbackSlugs[i]),
    images: items.map((p, i) => p.cover || fallbackImages[i]),
    details: items.map((p, i) => ({
      surface: localizeUnit(p, "surface", fallbackDetails?.[i]?.surface),
      duration: localizeUnit(p, "duration", fallbackDetails?.[i]?.duration),
      year: p.y ?? fallbackDetails?.[i]?.year,
      client: pickLocalized(p, "client", fallbackDetails?.[i]?.client),
      longDesc: pickLocalized(p, "longDesc", fallbackDetails?.[i]?.longDesc),
      beforeUrl: p.beforeUrl ?? fallbackDetails?.[i]?.beforeUrl,
      afterUrl: p.afterUrl ?? fallbackDetails?.[i]?.afterUrl,
    })),
    galleries: items.map((p, i) =>
      Array.isArray(p.gallery) && p.gallery.length > 0
        ? p.gallery.filter(Boolean)
        : (fallbackGalleries?.[i] || [])
    ),
  };
}

export function resolveHeroImage(content, fallback) {
  return content?.hero?.image_url || fallback;
}
